import { Typography } from '@capsule/consumer-design-system';

import { Form, Formik } from 'formik';
import { useHistory, useParams } from 'react-router';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';

import Spinner from 'src/app/components/Elements/Spinner';
import { FormikCheckboxTick } from 'src/app/components/Form/FormikCheckboxTick';
import { FormikTextarea } from 'src/app/components/Form/FormikTextarea';
import { editDeliveryAddressRequestBodyTransform } from 'src/app/components/Pages/Order/FailedOrderReschedule/utils';
import { useDeliveryAddressUpdate } from 'src/app/hooks/data/deliveryAddress';
import { useGetOrder } from 'src/app/hooks/data/order';
import { useRepeatFailureOrderRescheduleFormData } from 'src/app/hooks/feature_data/useRepeatFailureOrderRescheduleFormData';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { rescheduleDeliveryWindows, rescheduleOrder } from 'src/app/router/paths';

import { BuildingIcon, ClipboardIcon, PhoneIcon } from './icons';
import { FailedOrderRescheduleLayout } from './Layout/FailedOrderRescheduleLayout';
import type { RescheduleOrderPathParams } from './types';
import { RescheduleOrderStepName } from './types';

const DELIVERY_INSTRUCTIONS_MAX_LENGTH = 500;

const UnorderedList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  color: ${({ theme }) => theme.colors.capsuleBlue50};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.s3};

  & > li {
    display: flex;
    justify-content: flex-start;
    align-items: center;
    gap: ${({ theme }) => theme.space.s2};
  }

  & > li > svg {
    width: 2.4rem;
    height: 2.4rem;
  }
`;

const schema = Yup.object().shape({
  instructions: Yup.string().default('').max(DELIVERY_INSTRUCTIONS_MAX_LENGTH),
  is_doorman: Yup.boolean().default(false),
});

const schemaDefaultValues = schema.cast({});

const formId = 'rovaf'; // reschedule-order-verify-address-form

export const VerifyFailedOrderAddressStep = () => {
  useSegmentEvent('viewFailedAddressDeliveryInstructions', { shouldTrackOnMount: true });
  const trackSubmitDeliveryInstructionsEdit = useSegmentEvent('submitDeliveryInstructionsEdit', {
    shouldTrackOnMount: false,
  });
  const trackEditDeliveryInstructionsSuccess = useSegmentEvent('editDeliveryInstructionsSuccessful', {
    shouldTrackOnMount: false,
  });
  const trackEditDeliveryInstructionsFailure = useSegmentEvent('editDeliveryInstructionsFailed', {
    shouldTrackOnMount: false,
  });

  const { push } = useHistory();
  const { orderId } = useParams<RescheduleOrderPathParams>();
  const { data: order, isLoading: isLoadingOrder } = useGetOrder(orderId);
  const { mutateAsync: asyncUpdateDeliveryAddress, isLoading: isLoadingDeliveryAddress } = useDeliveryAddressUpdate();

  const { persistedFormData, setPersistedFormData } = useRepeatFailureOrderRescheduleFormData();

  const initialValues = {
    instructions:
      persistedFormData?.instructions ?? order?.deliveryAddress?.instructions ?? schemaDefaultValues.instructions,
    is_doorman: persistedFormData?.is_doorman ?? order?.deliveryAddress?.isDoorman ?? schemaDefaultValues.is_doorman,
  };

  return (
    <Formik
      validationSchema={schema}
      initialValues={initialValues}
      enableReinitialize={false}
      onSubmit={async (values, { setSubmitting }) => {
        setSubmitting(true);

        const analyticsPayload = {
          source: 'Reschedule Delivery Instructions Edit',
          delivery_address_id: order!.deliveryAddress.id!,
          updated_doorman: values.is_doorman !== initialValues.is_doorman,
          updated_instructions: values.instructions !== initialValues.instructions,
        };

        trackSubmitDeliveryInstructionsEdit(analyticsPayload);

        try {
          setPersistedFormData(values);

          if (values.is_doorman) {
            trackEditDeliveryInstructionsSuccess(analyticsPayload);
            push(rescheduleOrder.toPath({ orderId, stepName: RescheduleOrderStepName.DoormanConfirmation }));
          } else {
            await asyncUpdateDeliveryAddress({
              ...editDeliveryAddressRequestBodyTransform(order!.deliveryAddress),
              instructions: values.instructions,
              isDoorman: values.is_doorman,
            });
            trackEditDeliveryInstructionsSuccess(analyticsPayload);
            push(rescheduleDeliveryWindows.toPath({ orderId }));
          }
        } catch (e: any) {
          trackEditDeliveryInstructionsFailure({
            error_code: e?.code ?? 'Unknown Error Code',
            error_summary: e?.message ?? 'Unknown Error Message',
          });

          setSubmitting(false);
        }
      }}
    >
      {({ isSubmitting, values }) => {
        const isLoading = isSubmitting || isLoadingDeliveryAddress || isLoadingOrder;
        const hasFormValue = Object.values(values).some(value => !!value);

        return (
          <FailedOrderRescheduleLayout
            title="Add delivery instructions"
            description={
              isLoading ? (
                <Spinner centered />
              ) : (
                <>
                  <p
                    css={css`
                      margin-bottom: ${({ theme }) => theme.space.s3};
                    `}
                  >
                    Provide details to help our courier find you:
                  </p>

                  <UnorderedList>
                    <li>
                      <BuildingIcon /> Access code
                    </li>
                    <li>
                      <PhoneIcon /> Phone numbers
                    </li>
                    <li>
                      <ClipboardIcon /> Other entry instructions
                    </li>
                  </UnorderedList>
                </>
              )
            }
            cta1={{
              children: hasFormValue ? 'Continue' : 'Save Without Instructions',
              isSubmitting: isLoading,
              variant: hasFormValue ? undefined : 'outline-lg',
              form: formId,
            }}
          >
            <Form noValidate id={formId}>
              {!isLoading && (
                <>
                  <FormikTextarea
                    name="instructions"
                    label={{
                      content: 'Delivery Instructions',
                      isHidden: false,
                    }}
                    enterKeyHint="done"
                    onKeyDown={event => {
                      // We want to blur off the textarea on "Enter" instead of creating a new line.
                      if (event.key === 'Enter' && event.shiftKey !== true) {
                        event.preventDefault();
                        event.stopPropagation();
                        event.currentTarget.blur();
                      }
                    }}
                    maxLength={DELIVERY_INSTRUCTIONS_MAX_LENGTH}
                    rows={2}
                  />

                  <Typography
                    as="p"
                    variant="overline"
                    css={css`
                      color: ${({ theme }) => theme.colors.capsuleGreen60};
                      margin-top: ${({ theme }) => theme.space.s5};
                      margin-bottom: ${({ theme }) => theme.space.s2};
                    `}
                    aria-hidden="true"
                  >
                    Doorman
                  </Typography>

                  <FormikCheckboxTick
                    name="is_doorman"
                    label={{ content: 'Leave with doorman', isHidden: false }}
                    defaultChecked={values.is_doorman}
                  />
                </>
              )}
            </Form>
          </FailedOrderRescheduleLayout>
        );
      }}
    </Formik>
  );
};
